// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import {useContextMenuHoverState} from '@app/features/app/hooks/useContextMenuHoverState';
import {isMediaOnlyEmbed} from '@app/features/channel/components/embeds/EmbedRenderUtils';
import {MessageActionBar, MessageActionBarCore} from '@app/features/channel/components/MessageActionBar';
import {MessageActionBottomSheet} from '@app/features/channel/components/MessageActionBottomSheet';
import {requestDeleteMessage} from '@app/features/channel/components/MessageActionUtils';
import {MessageViewContextProvider} from '@app/features/channel/components/MessageViewContext';
import type {Channel} from '@app/features/channel/models/Channel';
import DeveloperOptions from '@app/features/devtools/state/DeveloperOptions';
import {parse} from '@app/features/messaging/components/markdown/renderers';
import {MarkdownContext} from '@app/features/messaging/components/markdown/renderers/RendererTypes';
import type {Message as MessageModel} from '@app/features/messaging/models/MessagingMessage';
import MessageEdit from '@app/features/messaging/state/MessageEdit';
import MessageFocus from '@app/features/messaging/state/MessageFocus';
import MessageReply from '@app/features/messaging/state/MessageReply';
import {getMessageComponent} from '@app/features/messaging/utils/MessageComponentUtils';
import {getParserFlagsForContext} from '@app/features/messaging/utils/markdown/MarkdownParserFlags';
import {parseAndRenderToPlaintext} from '@app/features/messaging/utils/markdown/Plaintext';
import {NodeType} from '@app/features/messaging/utils/markdown/parser/Enums';
import {SystemMessageUtils} from '@app/features/messaging/utils/SystemMessageUtils';
import {subscribeWindowFocus} from '@app/features/platform/utils/WindowFocusBroadcast';
import * as ReadStateCommands from '@app/features/read_state/commands/ReadStateCommands';
import styles from '@app/features/theme/styles/Message.module.css';
import {MessageContextMenu} from '@app/features/ui/action_menu/MessageContextMenu';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import KeyboardMode from '@app/features/ui/state/KeyboardMode';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import UserSettings from '@app/features/user/state/UserSettings';
import Users from '@app/features/user/state/Users';
import * as DateUtils from '@app/features/user/utils/DateFormatting';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {FLUXERBOT_ID} from '@fluxer/constants/src/AppConstants';
import {MessagePreviewContext, MessageStates, MessageTypes} from '@fluxer/constants/src/ChannelConstants';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

const ATTACHMENT_DESCRIPTOR = msg({
	message: 'attachment',
	comment:
		'Screen-reader fragment listing one attachment on a message. Lowercase because it appears inside a longer sentence.',
});
const ATTACHMENTS_DESCRIPTOR = msg({
	message: '{length} attachments',
	comment: 'Screen-reader fragment listing multiple attachments on a message. length is the count.',
});
const STICKER_DESCRIPTOR = msg({
	message: 'sticker',
	comment:
		'Screen-reader fragment listing one sticker on a message. Lowercase because it appears inside a longer sentence.',
});
const STICKERS_DESCRIPTOR = msg({
	message: '{length} stickers',
	comment: 'Screen-reader fragment listing multiple stickers on a message. length is the count.',
});
const EMBED_DESCRIPTOR = msg({
	message: 'embed',
	comment:
		'Screen-reader fragment listing one embed on a message. Lowercase because it appears inside a longer sentence.',
});
const EMBEDS_DESCRIPTOR = msg({
	message: '{length} embeds',
	comment: 'Screen-reader fragment listing multiple embeds on a message. length is the count.',
});
const NO_TEXT_CONTENT_DESCRIPTOR = msg({
	message: 'no text content',
	comment:
		'Screen-reader fragment indicating the message has no text body. Lowercase because it appears inside a longer sentence.',
});
const MESSAGE_DESCRIPTOR = msg({
	message: 'message',
	comment: 'Fallback role label used in screen-reader message summaries.',
});
const MESSAGE_TYPE_NAMES: Record<number, string> = Object.fromEntries(
	Object.entries(MessageTypes).map(([name, value]) => [value, name]),
);
const shouldApplyGroupedLayout = (message: MessageModel, _prevMessage?: MessageModel) => {
	if (message.type !== MessageTypes.DEFAULT && message.type !== MessageTypes.REPLY) {
		return false;
	}
	return true;
};
const isDisplaySystemMessage = (message: MessageModel): boolean =>
	message.type !== MessageTypes.DEFAULT && message.type !== MessageTypes.REPLY;
const isActivationKey = (key: string) => key === 'Enter' || key === ' ' || key === 'Spacebar' || key === 'Space';
const MESSAGE_ARIA_PARSER_FLAGS = getParserFlagsForContext(MarkdownContext.STANDARD_WITHOUT_JUMBO);
const MAX_ARIA_MESSAGE_TEXT_LENGTH = 220;
const LONG_PRESS_DELAY = 500;
const MOVEMENT_THRESHOLD = 10;
const SWIPE_VELOCITY_THRESHOLD = 0.4;
const HIGHLIGHT_DELAY = 100;
const SUPPRESS_POST_LONG_PRESS_CLICK_MS = 750;
const NESTED_LONG_PRESS_OWNER_SELECTOR = '[data-long-press-owner="true"]';
const mobileLongPressScrollCancelHandlers = new Set<() => void>();

let mobileLongPressScrollListenerAttached = false;

const handleMobileLongPressWindowScroll = () => {
	for (const handler of mobileLongPressScrollCancelHandlers) {
		handler();
	}
};
const subscribeMobileLongPressScrollCancel = (handler: () => void): (() => void) => {
	mobileLongPressScrollCancelHandlers.add(handler);
	if (!mobileLongPressScrollListenerAttached) {
		window.addEventListener('scroll', handleMobileLongPressWindowScroll, {capture: true, passive: true});
		mobileLongPressScrollListenerAttached = true;
	}
	return () => {
		mobileLongPressScrollCancelHandlers.delete(handler);
		if (mobileLongPressScrollListenerAttached && mobileLongPressScrollCancelHandlers.size === 0) {
			window.removeEventListener('scroll', handleMobileLongPressWindowScroll, {capture: true});
			mobileLongPressScrollListenerAttached = false;
		}
	};
};
const handleAltClickEvent = (event: React.MouseEvent, message: MessageModel) => {
	if (!event.altKey) return;
	ReadStateCommands.markAsUnread(message.channelId, message.id);
};
const handleAltKeyboardEvent = (event: React.KeyboardEvent, message: MessageModel) => {
	if (!event.altKey || !isActivationKey(event.key)) {
		return;
	}
	event.preventDefault();
	ReadStateCommands.markAsUnread(message.channelId, message.id);
};
const getContextMenuLinkUrl = (target: EventTarget | null): string | undefined => {
	if (!(target instanceof HTMLElement)) {
		return undefined;
	}
	const anchor = target.closest('a');
	if (anchor?.href) {
		return anchor.href;
	}
	const mediaTarget = target.closest('[data-message-emoji="true"], [data-message-sticker="true"]');
	if (!mediaTarget) {
		return undefined;
	}
	const imageElement =
		mediaTarget instanceof HTMLImageElement ? mediaTarget : mediaTarget.querySelector<HTMLImageElement>('img');
	if (!imageElement) {
		return undefined;
	}
	const imageUrl = imageElement.currentSrc || imageElement.src;
	return imageUrl || undefined;
};
const getElementFromEventTarget = (target: EventTarget | null): Element | null => {
	if (target instanceof Element) return target;
	if (target instanceof Node) return target.parentElement;
	return null;
};
const isNestedLongPressOwnerTarget = (target: EventTarget | null): boolean =>
	getElementFromEventTarget(target)?.closest(NESTED_LONG_PRESS_OWNER_SELECTOR) != null;
const normalizeAriaLabelText = (text: string): string => text.replace(/\s+/g, ' ').trim();
const truncateAriaLabelText = (text: string): string => {
	const normalized = normalizeAriaLabelText(text);
	if (normalized.length <= MAX_ARIA_MESSAGE_TEXT_LENGTH) {
		return normalized;
	}
	return `${normalized.slice(0, MAX_ARIA_MESSAGE_TEXT_LENGTH - 1).trimEnd()}...`;
};

export type MessageBehaviorOverrides = Partial<{
	mobileLayoutEnabled: boolean;
	messageGroupSpacing: number;
	messageDisplayCompact: boolean;
	prefersReducedMotion: boolean;
	isEditing: boolean;
	isReplying: boolean;
	isHighlight: boolean;
	forceUnknownMessageType: boolean;
	contextMenuOpen: boolean;
	disableContextMenu: boolean;
	disableContextMenuTracking: boolean;
}>;

interface MessageProps {
	channel: Channel;
	message: MessageModel;
	prevMessage?: MessageModel;
	onEdit?: (targetNode: HTMLElement) => void;
	previewContext?: keyof typeof MessagePreviewContext;
	shouldGroup?: boolean;
	previewOverrides?: {
		usernameColor?: string;
		displayName?: string;
	};
	removeTopSpacing?: boolean;
	isJumpTarget?: boolean;
	previewMode?: boolean;
	behaviorOverrides?: MessageBehaviorOverrides;
	compact?: boolean;
	idPrefix?: string;
	readonlyPreview?: boolean;
	onHeadingActivate?: () => void;
}

export const Message: React.FC<MessageProps> = observer((props) => {
	const {
		channel,
		message,
		prevMessage,
		onEdit,
		previewContext,
		shouldGroup = false,
		previewOverrides,
		removeTopSpacing = false,
		isJumpTarget = false,
		previewMode,
		behaviorOverrides,
		compact,
		idPrefix = 'message',
		readonlyPreview,
		onHeadingActivate,
	} = props;
	const {i18n} = useLingui();
	const [showActionBar, setShowActionBar] = useState(false);
	const [isLongPressing, setIsLongPressing] = useState(false);
	const [isHoveringDesktop, setIsHoveringDesktop] = useState(false);
	const [isFocusedWithin, setIsFocusedWithin] = useState(false);
	const [isPopoutOpen, setIsPopoutOpen] = useState(false);
	const messageRef = useRef<HTMLDivElement | null>(null);
	const disableContextMenuTracking = behaviorOverrides?.disableContextMenuTracking ?? false;
	const trackedContextMenuOpen = useContextMenuHoverState(messageRef, !disableContextMenuTracking);
	const contextMenuOpen = disableContextMenuTracking
		? (behaviorOverrides?.contextMenuOpen ?? false)
		: trackedContextMenuOpen;
	const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
	const unsubscribeLongPressScrollCancelRef = useRef<(() => void) | null>(null);
	const wasEditingInPreviousUpdateRef = useRef(false);
	const mobileLayoutEnabled = behaviorOverrides?.mobileLayoutEnabled ?? MobileLayout.isEnabled();
	const messageDisplayCompact =
		compact ?? behaviorOverrides?.messageDisplayCompact ?? UserSettings.getMessageDisplayCompact();
	const prefersReducedMotion = behaviorOverrides?.prefersReducedMotion ?? Accessibility.useReducedMotion;
	const isEditing = behaviorOverrides?.isEditing ?? MessageEdit.isEditing(message.channelId, message.id);
	const isReplying = behaviorOverrides?.isReplying ?? MessageReply.isReplying(message.channelId, message.id);
	const isHighlight = behaviorOverrides?.isHighlight ?? MessageReply.isHighlight(message.id);
	const forceUnknownMessageType =
		behaviorOverrides?.forceUnknownMessageType ?? DeveloperOptions.forceUnknownMessageType;
	const messageGroupSpacing =
		behaviorOverrides?.messageGroupSpacing ?? Accessibility.getMessageGroupSpacingValue(messageDisplayCompact);
	const authorName =
		previewOverrides?.displayName || NicknameUtils.getNickname(message.author, channel.guildId, channel.id);
	const messageAriaLabel = useMemo(() => {
		const timeLabel = DateUtils.getFormattedDateTime(message.timestamp);
		const systemText = message.isSystemMessage() ? SystemMessageUtils.stringify(message, i18n) : null;
		let text =
			systemText ||
			parseAndRenderToPlaintext(message.content, MESSAGE_ARIA_PARSER_FLAGS, {
				channelId: channel.id,
				preserveMarkdown: false,
				includeEmojiNames: true,
				i18n,
			});
		if (!text && message.attachments.length > 0) {
			text =
				message.attachments.length === 1
					? i18n._(ATTACHMENT_DESCRIPTOR)
					: i18n._(ATTACHMENTS_DESCRIPTOR, {length: message.attachments.length});
		}
		if (!text && message.stickerItems.length > 0) {
			text =
				message.stickerItems.length === 1
					? i18n._(STICKER_DESCRIPTOR)
					: i18n._(STICKERS_DESCRIPTOR, {length: message.stickerItems.length});
		}
		if (!text && message.embeds.length > 0) {
			text =
				message.embeds.length === 1
					? i18n._(EMBED_DESCRIPTOR)
					: i18n._(EMBEDS_DESCRIPTOR, {length: message.embeds.length});
		}
		if (!text) {
			text = i18n._(NO_TEXT_CONTENT_DESCRIPTOR);
		}
		return `${authorName}, ${truncateAriaLabelText(text)}, ${timeLabel}, ${i18n._(MESSAGE_DESCRIPTOR)}`;
	}, [
		authorName,
		channel.id,
		i18n,
		message,
		message.attachments.length,
		message.content,
		message.embeds.length,
		message.stickerItems.length,
		message.timestamp,
	]);
	const handleAltClick = useCallback(
		(event: React.MouseEvent) => {
			handleAltClickEvent(event, message);
		},
		[message],
	);
	const handleAltKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			handleAltKeyboardEvent(event, message);
		},
		[message],
	);
	const handleDelete = useCallback(
		(bypassConfirm = false) => {
			requestDeleteMessage(message, i18n, bypassConfirm);
		},
		[i18n, message],
	);
	const handleContextMenu = useCallback(
		(event: React.MouseEvent) => {
			if (behaviorOverrides?.disableContextMenu) {
				event.preventDefault();
				return;
			}
			if (
				(previewContext && previewContext !== MessagePreviewContext.LIST_POPOUT) ||
				message.state === MessageStates.SENDING ||
				isEditing
			) {
				return;
			}
			event.preventDefault();
			if (mobileLayoutEnabled) {
				return;
			}
			MessageFocus.holdContextFocus(channel.id, message.id, message, channel);
			const linkUrl = getContextMenuLinkUrl(event.target);
			ContextMenuCommands.openFromEvent(event, (props) => (
				<MessageContextMenu
					message={message}
					sourceChannel={channel}
					onClose={props.onClose}
					onDelete={handleDelete}
					linkUrl={linkUrl}
					data-flx="channel.message.handle-context-menu.message-context-menu"
				/>
			));
		},
		[
			previewContext,
			message,
			channel,
			isEditing,
			mobileLayoutEnabled,
			handleDelete,
			behaviorOverrides?.disableContextMenu,
		],
	);
	const touchStartPos = useRef<{x: number; y: number} | null>(null);
	const velocitySamples = useRef<Array<{x: number; y: number; timestamp: number}>>([]);
	const highlightTimerRef = useRef<NodeJS.Timeout | null>(null);
	const suppressClickUntilRef = useRef(0);
	const unsubscribeLongPressScrollCancel = useCallback(() => {
		unsubscribeLongPressScrollCancelRef.current?.();
		unsubscribeLongPressScrollCancelRef.current = null;
	}, []);
	const clearLongPressState = useCallback(() => {
		unsubscribeLongPressScrollCancel();
		if (longPressTimerRef.current) {
			clearTimeout(longPressTimerRef.current);
			longPressTimerRef.current = null;
		}
		if (highlightTimerRef.current) {
			clearTimeout(highlightTimerRef.current);
			highlightTimerRef.current = null;
		}
		touchStartPos.current = null;
		velocitySamples.current = [];
		setIsLongPressing(false);
	}, [unsubscribeLongPressScrollCancel]);
	const calculateVelocity = useCallback((): number => {
		const samples = velocitySamples.current;
		if (samples.length < 2) return 0;
		const now = performance.now();
		let firstRecentIndex = samples.length - 1;
		for (let i = samples.length - 1; i >= 0; i--) {
			if (now - samples[i].timestamp >= 100) {
				break;
			}
			firstRecentIndex = i;
		}
		if (samples.length - firstRecentIndex < 2) return 0;
		const first = samples[firstRecentIndex];
		const last = samples[samples.length - 1];
		const dt = last.timestamp - first.timestamp;
		if (dt === 0) return 0;
		const dx = last.x - first.x;
		const dy = last.y - first.y;
		return Math.sqrt(dx * dx + dy * dy) / dt;
	}, []);
	const handleLongPressStart = useCallback(
		(event: React.TouchEvent) => {
			if (!mobileLayoutEnabled || previewContext || isNestedLongPressOwnerTarget(event.target)) {
				return;
			}
			const touch = event.touches[0];
			if (!touch) return;
			unsubscribeLongPressScrollCancel();
			unsubscribeLongPressScrollCancelRef.current = subscribeMobileLongPressScrollCancel(() => {
				if (touchStartPos.current) {
					clearLongPressState();
				}
			});
			touchStartPos.current = {x: touch.clientX, y: touch.clientY};
			velocitySamples.current = [{x: touch.clientX, y: touch.clientY, timestamp: performance.now()}];
			highlightTimerRef.current = setTimeout(() => {
				if (touchStartPos.current) {
					setIsLongPressing(true);
				}
				highlightTimerRef.current = null;
			}, HIGHLIGHT_DELAY);
			longPressTimerRef.current = setTimeout(() => {
				if (touchStartPos.current) {
					suppressClickUntilRef.current = performance.now() + SUPPRESS_POST_LONG_PRESS_CLICK_MS;
					setShowActionBar(true);
					setIsLongPressing(false);
				}
				clearLongPressState();
			}, LONG_PRESS_DELAY);
		},
		[mobileLayoutEnabled, previewContext, clearLongPressState, unsubscribeLongPressScrollCancel],
	);
	const handleLongPressEnd = useCallback(() => {
		clearLongPressState();
	}, [clearLongPressState]);
	const handleLongPressMove = useCallback(
		(event: React.TouchEvent) => {
			if (!touchStartPos.current) return;
			const touch = event.touches[0];
			if (!touch) return;
			velocitySamples.current.push({x: touch.clientX, y: touch.clientY, timestamp: performance.now()});
			if (velocitySamples.current.length > 10) {
				velocitySamples.current.shift();
			}
			const deltaX = Math.abs(touch.clientX - touchStartPos.current.x);
			const deltaY = Math.abs(touch.clientY - touchStartPos.current.y);
			if (deltaX > MOVEMENT_THRESHOLD || deltaY > MOVEMENT_THRESHOLD) {
				clearLongPressState();
				return;
			}
			const velocity = calculateVelocity();
			if (velocity > SWIPE_VELOCITY_THRESHOLD) {
				clearLongPressState();
			}
		},
		[clearLongPressState, calculateVelocity],
	);
	const handleClickCapture = useCallback(
		(event: React.MouseEvent<HTMLDivElement>) => {
			if (!mobileLayoutEnabled || performance.now() > suppressClickUntilRef.current) {
				return;
			}
			event.preventDefault();
			event.stopPropagation();
		},
		[mobileLayoutEnabled],
	);
	const keyboardModeEnabled = KeyboardMode.keyboardModeEnabled;
	const handleFocusWithin = useCallback(() => {
		if (!keyboardModeEnabled) {
			return;
		}
		setIsFocusedWithin(true);
		MessageFocus.focusMessage(channel.id, message.id, message, channel);
	}, [channel, message, keyboardModeEnabled]);
	const handleBlurWithin = useCallback(() => {
		setIsFocusedWithin(false);
		MessageFocus.blurMessage(channel.id, message.id);
	}, [channel.id, message.id]);
	useEffect(() => {
		if (mobileLayoutEnabled || !messageRef.current) return;
		const element = messageRef.current;
		const syncHoverState = () => {
			setIsHoveringDesktop(element.matches(':hover'));
		};
		const handleMouseEnter = () => {
			setIsHoveringDesktop(true);
		};
		const handleMouseLeave = () => {
			setIsHoveringDesktop(false);
		};
		element.addEventListener('mouseenter', handleMouseEnter);
		element.addEventListener('mouseleave', handleMouseLeave);
		const unsubscribeFocus = subscribeWindowFocus(syncHoverState);
		const rafId = requestAnimationFrame(syncHoverState);
		return () => {
			cancelAnimationFrame(rafId);
			element.removeEventListener('mouseenter', handleMouseEnter);
			element.removeEventListener('mouseleave', handleMouseLeave);
			unsubscribeFocus();
		};
	}, [mobileLayoutEnabled, keyboardModeEnabled]);
	useEffect(() => {
		if (!keyboardModeEnabled) return;
		if (contextMenuOpen) {
			MessageFocus.holdContextFocus(channel.id, message.id, message, channel);
			return;
		}
		MessageFocus.releaseContextFocus(channel.id, message.id);
		if (!isFocusedWithin) {
			MessageFocus.clearFocusedMessageIfMatches(channel.id, message.id);
		}
	}, [channel, contextMenuOpen, isFocusedWithin, keyboardModeEnabled, message, message.id]);
	useEffect(() => {
		const wasEditing = wasEditingInPreviousUpdateRef.current;
		const justStartedEditing = !wasEditing && isEditing;
		if (justStartedEditing && onEdit && messageRef.current) {
			onEdit(messageRef.current);
		}
		wasEditingInPreviousUpdateRef.current = isEditing;
	}, [isEditing, onEdit]);
	useEffect(() => {
		return () => {
			unsubscribeLongPressScrollCancel();
			if (longPressTimerRef.current) {
				clearTimeout(longPressTimerRef.current);
			}
			if (highlightTimerRef.current) {
				clearTimeout(highlightTimerRef.current);
			}
		};
	}, [unsubscribeLongPressScrollCancel]);
	const isHovering = mobileLayoutEnabled ? false : isHoveringDesktop;
	useEffect(() => {
		if (!keyboardModeEnabled) {
			setIsFocusedWithin(false);
			return;
		}
		const activeElement = messageRef.current?.ownerDocument?.activeElement ?? document.activeElement;
		if (messageRef.current && activeElement && messageRef.current.contains(activeElement)) {
			setIsFocusedWithin(true);
		}
	}, [keyboardModeEnabled]);
	const actionBarActive = previewMode
		? true
		: isHovering || (keyboardModeEnabled && isFocusedWithin) || isPopoutOpen || contextMenuOpen;
	const messageContextValue = useMemo(
		() => ({
			channel,
			message,
			handleDelete,
			shouldGroup,
			isHovering,
			messageDisplayCompact,
			previewContext,
			previewOverrides,
			previewPermissions: previewMode
				? {
						isDM: false,
						canSendMessages: true,
						canAddReactions: true,
						canEditMessage: true,
						canDeleteMessage: true,
						canDeleteAttachment: true,
						canPinMessage: true,
						canForwardMessage: true,
						canSuppressEmbeds: true,
						shouldRenderSuppressEmbeds: false,
					}
				: undefined,
			onPopoutToggle: setIsPopoutOpen,
			readonlyPreview,
			onHeadingActivate,
		}),
		[
			channel,
			message,
			handleDelete,
			shouldGroup,
			isHovering,
			messageDisplayCompact,
			previewContext,
			previewOverrides,
			previewMode,
			setIsPopoutOpen,
			readonlyPreview,
			onHeadingActivate,
		],
	);
	const messageComponent = (
		<MessageViewContextProvider value={messageContextValue} data-flx="channel.message.message-view-context-provider">
			{getMessageComponent(message, channel, forceUnknownMessageType)}
		</MessageViewContextProvider>
	);
	const {nodes: astNodes} = useMemo(
		() =>
			parse({
				content: message.content,
				context: MarkdownContext.STANDARD_WITH_JUMBO,
			}),
		[message.content],
	);
	const shouldHideContent =
		UserSettings.getRenderEmbeds() &&
		message.embeds.length > 0 &&
		message.embeds.every(isMediaOnlyEmbed) &&
		astNodes.length === 1 &&
		astNodes[0].type === NodeType.Link &&
		!message.suppressEmbeds;
	const shouldDisableHoverBackground = (prefersReducedMotion && !isEditing) || readonlyPreview;
	const isKeyboardFocused = keyboardModeEnabled && isFocusedWithin;
	const shouldApplySpacing = !shouldGroup && !removeTopSpacing && previewContext !== MessagePreviewContext.LIST_POPOUT;
	const systemFollowsSystem = Boolean(
		shouldGroup && prevMessage && isDisplaySystemMessage(prevMessage) && isDisplaySystemMessage(message),
	);
	const messageClasses = useMemo(
		() =>
			clsx(
				messageDisplayCompact ? styles.messageCompact : styles.message,
				shouldDisableHoverBackground && styles.messageNoHover,
				isEditing && styles.messageEditing,
				!messageDisplayCompact &&
					shouldGroup &&
					shouldApplyGroupedLayout(message, prevMessage) &&
					styles.messageGrouped,
				systemFollowsSystem && styles.systemMessageFollowsSystem,
				!previewContext && message.isMentioned() && styles.messageMentioned,
				!previewContext &&
					(isReplying || isHighlight || isJumpTarget) &&
					(isReplying ? styles.messageReplying : styles.messageHighlight),
				message.type === MessageTypes.CLIENT_SYSTEM && message.author.id === FLUXERBOT_ID && styles.messageClientSystem,
				isLongPressing && styles.messageLongPress,
				!previewContext && (contextMenuOpen || isPopoutOpen) && styles.contextMenuActive,
				previewContext && styles.messagePreview,
				mobileLayoutEnabled && styles.mobileLayout,
				!messageDisplayCompact &&
					(!message.content || shouldHideContent) &&
					!isEditing &&
					message.isUserMessage() &&
					styles.messageNoText,
				isKeyboardFocused && styles.keyboardFocused,
				isKeyboardFocused && 'keyboard-focus-active',
				shouldApplySpacing && previewContext && styles.messagePreviewSpacing,
			),
		[
			messageDisplayCompact,
			shouldDisableHoverBackground,
			isEditing,
			systemFollowsSystem,
			shouldGroup,
			message,
			prevMessage,
			previewContext,
			isReplying,
			isHighlight,
			isJumpTarget,
			isLongPressing,
			contextMenuOpen,
			isPopoutOpen,
			mobileLayoutEnabled,
			shouldHideContent,
			isKeyboardFocused,
			shouldApplySpacing,
		],
	);
	const shouldShowActionBar = useMemo(
		() =>
			!previewContext &&
			!readonlyPreview &&
			message.state !== MessageStates.SENDING &&
			!isEditing &&
			!mobileLayoutEnabled,
		[previewContext, readonlyPreview, message.state, isEditing, mobileLayoutEnabled],
	);
	const shouldShowBottomSheet = useMemo(
		() =>
			mobileLayoutEnabled && showActionBar && !previewContext && message.state !== MessageStates.SENDING && !isEditing,
		[mobileLayoutEnabled, showActionBar, previewContext, message.state, isEditing],
	);
	const articleStyle = useMemo<React.CSSProperties>(
		() => ({
			touchAction: 'pan-y',
			WebkitUserSelect: 'text',
			userSelect: 'text',
			marginTop: shouldApplySpacing && previewContext ? `${messageGroupSpacing}px` : undefined,
		}),
		[shouldApplySpacing, previewContext, messageGroupSpacing],
	);
	return (
		<>
			<FocusRing data-flx="channel.message.focus-ring">
				<div
					role="article"
					aria-label={messageAriaLabel}
					id={`${idPrefix}-${channel.id}-${message.id}`}
					data-message-id={message.id}
					data-channel-id={channel.id}
					data-flx-message-id={message.id}
					data-flx-message-type={MESSAGE_TYPE_NAMES[message.type] ?? String(message.type)}
					data-flx-channel-id={channel.id}
					data-flx-guild-id={channel.guildId ?? undefined}
					data-flx-author-id={message.author.id}
					data-flx-author-username={NicknameUtils.formatNameForStreamerMode(message.author.username)}
					data-flx-author-name={authorName}
					data-flx-author-self={message.author.id === Users.currentUserId ? 'true' : undefined}
					data-flx-author-bot={message.author.bot ? 'true' : undefined}
					data-flx-author-webhook={message.webhookId != null ? 'true' : undefined}
					data-flx-reply={message.type === MessageTypes.REPLY || message.messageReference != null ? 'true' : undefined}
					data-flx-blocked={message.blocked ? 'true' : undefined}
					data-flx-pinned={message.pinned ? 'true' : undefined}
					data-flx-call={message.type === MessageTypes.CALL ? 'true' : undefined}
					data-flx-system={message.isSystemMessage() ? 'true' : undefined}
					data-flx-mentioned={message.isMentioned() ? 'true' : undefined}
					data-flx-edited={message.editedTimestamp != null ? 'true' : undefined}
					data-flx-compact={messageDisplayCompact ? 'true' : undefined}
					data-flx-grouped={shouldGroup && shouldApplyGroupedLayout(message, prevMessage) ? 'true' : undefined}
					tabIndex={keyboardModeEnabled ? 0 : undefined}
					className={messageClasses}
					ref={messageRef}
					onClickCapture={handleClickCapture}
					onClick={handleAltClick}
					onKeyDown={handleAltKeyDown}
					onFocus={handleFocusWithin}
					onBlur={handleBlurWithin}
					onContextMenu={handleContextMenu}
					onTouchStart={handleLongPressStart}
					onTouchEnd={handleLongPressEnd}
					onTouchMove={handleLongPressMove}
					style={articleStyle}
					data-flx="channel.message.article.alt-click"
				>
					{messageComponent}
					{shouldShowActionBar &&
						actionBarActive &&
						(previewMode ? (
							<MessageActionBarCore
								message={message}
								handleDelete={handleDelete}
								permissions={{
									channel,
									canSendMessages: true,
									canAddReactions: true,
									canEditMessage: true,
									canDeleteMessage: true,
									canPinMessage: true,
									canForwardMessage: true,
									shouldRenderSuppressEmbeds: true,
								}}
								isSaved={false}
								developerMode={false}
								isHovering={actionBarActive}
								onPopoutToggle={setIsPopoutOpen}
								data-flx="channel.message.message-action-bar-core"
							/>
						) : (
							<MessageActionBar
								message={message}
								handleDelete={handleDelete}
								isHovering={actionBarActive}
								sourceChannel={channel}
								onPopoutToggle={setIsPopoutOpen}
								data-flx="channel.message.message-action-bar"
							/>
						))}
				</div>
			</FocusRing>
			{shouldShowBottomSheet && (
				<MessageActionBottomSheet
					isOpen={shouldShowBottomSheet}
					onClose={() => setShowActionBar(false)}
					message={message}
					sourceChannel={channel}
					handleDelete={handleDelete}
					data-flx="channel.message.message-action-bottom-sheet"
				/>
			)}
		</>
	);
});
